'use client';

import React, { useEffect, useMemo } from 'react';

import Image from 'next/image';
import { useRouter } from 'next/navigation';

import Modal, { ModalProps } from '@/components/Modal';
import Tab, { TabContent, TabLabel } from '@/components/Tab';
import { NEXT_MEDIA_PATH } from '@/config/env';
import { FoodType, Species } from '@/helpers/constants/shopping';
import useEzShip from '@/helpers/hooks/useEzShip';
import useForm from '@/helpers/hooks/useForm';
import useWebAlias from '@/helpers/hooks/useWebalias';
import cn from '@/helpers/utils/classNames';
import { useAppSelector } from '@/store';
import { useGetFoodLabelsQuery } from '@/store/api/foodLabels';
import { CartItem } from '@/types/Cart';
import { ApplyFoodLabelPayload, FoodLabel } from '@/types/FoodLabel';

import Button from '../FormFields/Button';
import Radio from '../FormFields/Radio';
import Loader from '../Loader';
import { useShoppingCartContext } from '../Providers/ShoppingCartProvider';
import TextSmall from '../Typography/TextSmall';

interface ChooseFoodLabelModalProps extends Pick<ModalProps, 'isOpen' | 'onClose'> {
  cartItem: CartItem;
}

const foodLabelAdapter = (specie: Species, foodLabels?: Array<FoodLabel>) => {
  return foodLabels
    ?.filter(labelGroup => labelGroup.petSpecieID === specie)
    .map(labelGroup => ({
      ...labelGroup,
      petFoodLabelGroupDetails: labelGroup.petFoodLabelGroupDetails.filter(groupDetail => groupDetail.imageVisible),
    }));
};

const ChooseFoodLabelModal = ({ isOpen, onClose, cartItem }: ChooseFoodLabelModalProps) => {
  const { ezShipId } = useEzShip();
  const router = useRouter();

  const { dogLabels, catLabels } = useGetFoodLabelsQuery(undefined, {
    selectFromResult: ({ data }) => ({
      dogLabels: foodLabelAdapter(Species.DOG, data?.data),
      catLabels: foodLabelAdapter(Species.CAT, data?.data),
    }),
  });

  const { onApplyFoodLabel, isLoading } = useShoppingCartContext();
  const { generateURL, webalias } = useWebAlias();

  const { data, setFieldValue } = useForm<Pick<ApplyFoodLabelPayload, 'labelID' | 'petSpecieID'>, null>({
    initialState: { labelID: cartItem?.label?.labelId || 0, petSpecieID: cartItem?.label?.petSpecieID || 0 },
    errors: null,
  });

  const handleApplyFoodLabel = async () => {
    const payload: ApplyFoodLabelPayload = {
      itemCode: cartItem.itemCode || cartItem.id.toString(),
      itemID: cartItem.itemCode || cartItem.itemId,
      labelID: data.labelID,
      petSpecieID: data.petSpecieID,
      isActive: true,
    };

    onApplyFoodLabel(payload);
    onClose();
  };

  const handleSelectLabel = (labelID: number, petSpecieID: number) => {
    setFieldValue('labelID', labelID);
    setFieldValue('petSpecieID', petSpecieID);
  };

  const addFoodLabelToEzShipResp = useAppSelector(state => state.slices.ezShipSlice.foodLabel);

  const groups = useMemo(() => {
    const groupData = [];

    if (cartItem.foodType === FoodType.DOG) {
      groupData.push({
        key: 'dogLabels',
        labels: dogLabels,
      });
    }

    if (cartItem.foodType === FoodType.CAT) {
      groupData.push({
        key: 'catLabels',
        labels: catLabels,
      });
    }
    return groupData;
  }, [catLabels, dogLabels, cartItem]);

  const tabLabels = useMemo(() => {
    const labels = [];

    if (cartItem.foodType === FoodType.DOG) {
      labels.push(
        <TabLabel
          key="Dog"
          className="rounded-full"
          renderLabel={(isActive, onClick) => (
            <Button
              type="button"
              variant="outlined"
              scheme="secondary"
              onClick={e => {
                // setFieldValue('labelID', 0);
                // setFieldValue('petSpecieID', 0);

                if (onClick) {
                  onClick(e);
                }
              }}
              className={cn('mr-2 py-2 px-6 hover:bg-primary hover:text-white xxs:px-4 xxs:text-sm', {
                'bg-primary text-white': isActive,
              })}
            >
              Dog
            </Button>
          )}
        />
      );
    }

    if (cartItem.foodType === FoodType.CAT) {
      labels.push(
        <TabLabel
          key="Cat"
          className="rounded-full"
          renderLabel={(isActive, onClick) => (
            <Button
              type="button"
              variant="outlined"
              scheme="secondary"
              onClick={e => {
                // setFieldValue('labelID', 0);
                // setFieldValue('petSpecieID', 0);
                if (onClick) {
                  onClick(e);
                }
              }}
              className={cn('mr-2 py-2 px-6 hover:bg-primary hover:text-white xxs:px-4 xxs:text-sm', {
                'bg-primary text-white': isActive,
              })}
            >
              Cat
            </Button>
          )}
        />
      );
    }

    return labels;
  }, [cartItem]);

  useEffect(() => {
    if (ezShipId && addFoodLabelToEzShipResp?.isComplete) {
      onClose();
    }
  }, [addFoodLabelToEzShipResp, ezShipId, onClose]);

  return (
    <Modal
      className="relative w-[559px] max-w-xl"
      closeBtnClassName="scale-120 right-4 -top-4"
      isOpen={isOpen}
      onClose={onClose}
      title={
        <div className="flex flex-wrap items-center gap-2">
          <p>Choose Label</p>
          <p className="min-w-[100px] max-w-[274px] rounded-[999px] bg-[#f9f9f9] py-1 px-1 text-[10px] leading-[13px] text-primary">
            {cartItem.name}
          </p>
        </div>
      }
    >
      {addFoodLabelToEzShipResp?.isLoading && <Loader className="absolute rounded-md bg-transparent" />}
      <div>
        {/* <div className="mb-6 flex items-start gap-2">
          <h4 className="font-circular-std-medium text-[20px] text-[#534741]">Choose Label</h4>
          <p className="min-w-[100px] max-w-[274px] rounded-[999px] bg-[#f9f9f9] py-1 px-2 text-[10px] leading-[13px] text-primary">
            {cartItem.name}
          </p>
        </div> */}
        <div className="relative mb-4">
          <Button
            type="button"
            variant="outlined"
            scheme="secondary"
            onClick={e => {
              e.preventDefault();
              router.push(generateURL('/foodlabel/managelabels', webalias));
            }}
            className={cn(
              'absolute border-primary text-primary hover:bg-primary hover:text-white sm:right-0 sm:mr-0 sm:py-2 sm:px-6 lg:py-2 lg:px-6 xxs:px-4 xxs:text-sm xxs:text-primary'
            )}
          >
            Add New
          </Button>
          <Tab
            contentWrapperClassName="h-[300px] overflow-auto px-[6px]"
            labels={tabLabels}
            contents={groups.map(({ labels, key }) => (
              <TabContent key={key} className="font-light">
                <ul className="">
                  {labels?.map(({ petFoodLabelGroupID, name, petFoodLabelGroupDetails, petSpecieID }) => (
                    <li
                      key={petFoodLabelGroupID}
                      className={cn(
                        'mb-2 flex w-full cursor-pointer items-center justify-between rounded-[4px] border border-[#e5e5e5] py-2 px-4 font-circular-std-medium',
                        {
                          'border-primary': petFoodLabelGroupID === data.labelID,
                        }
                      )}
                      onClick={() => handleSelectLabel(petFoodLabelGroupID, petSpecieID)}
                    >
                      <Radio
                        id={petFoodLabelGroupID.toString()}
                        name="labelID"
                        defaultValue={petFoodLabelGroupID.toString()}
                        currentValue={data.labelID.toString()}
                        onChange={value => handleSelectLabel(+value, petSpecieID)}
                        containerClassName="items-center"
                      >
                        <TextSmall variant="dark">
                          <span className="font-bold">{name}</span>{' '}
                          <span className="text-grayscale-95">({petFoodLabelGroupID})</span>
                        </TextSmall>
                      </Radio>
                      <div className="flex items-center gap-2">
                        {petFoodLabelGroupDetails.map(({ petFoodLabelGroupDetailID, petImage }) => (
                          <Image
                            src={`${NEXT_MEDIA_PATH}${petImage}`}
                            alt="Label Image"
                            key={petFoodLabelGroupDetailID.toString()}
                            width={30}
                            height={30}
                            className="rounded-full"
                          />
                        ))}
                      </div>
                    </li>
                  ))}
                </ul>
              </TabContent>
            ))}
          />
        </div>
        <div className="flex justify-center gap-4">
          <Button variant="outlined" type="button" scheme="primary" className="basis-[50%]" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="filled"
            type="button"
            scheme="primary"
            className="basis-[50%]"
            disabled={!data.labelID || !data.petSpecieID || isLoading}
            onClick={handleApplyFoodLabel}
          >
            Apply
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default ChooseFoodLabelModal;
